import type { AxiosError } from 'axios';
import { type ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useVerifySharedLinkMutation } from '../api/mutations';
import { SharedLinkLoading } from './shared-link-loading';
import { SharedLinkUnavailable } from './shared-link-unavailable';

type Props = {
  children: ReactNode;
};

export const SharedLinkGuard = ({ children }: Props) => {
  const [searchParams] = useSearchParams();
  const signature = searchParams.get('sign');

  const [status, setStatus] = useState<'pending' | 'success' | 'error'>(
    'pending',
  );

  const verifyMutation = useVerifySharedLinkMutation();

  useEffect(() => {
    const verify = async () => {
      if (!signature) {
        setStatus('error');
        return;
      }

      try {
        const link = `${window.location.origin}${window.location.pathname}`;
        await verifyMutation.mutateAsync({ link, sign: signature });
        setStatus('success');
      } catch (err) {
        const axiosError = err as AxiosError<{ code: string }>;
        console.error(
          'SharedLinkGuard verification failed:',
          axiosError.response?.data?.code,
        );
        setStatus('error');
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  if (status === 'pending') {
    return <SharedLinkLoading />;
  }

  if (status === 'error') {
    return <SharedLinkUnavailable />;
  }

  return <>{children}</>;
};
